import { KATEX_IFRAME_HEAD } from "@/utils/math";
import { sanitizePreviewHtml } from "@/utils/sanitize";
import { useMemo } from "react";

export const PREVIEW_STYLES = `<style>
*{box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;font-size:14px;line-height:1.7;color:#1a1a1a;padding:1.5rem 2rem;margin:0;max-width:100%;overflow-x:hidden}
h1,h2,h3,h4,h5,h6{font-weight:600;margin:1.5em 0 .5em;line-height:1.3}
h1{font-size:1.5em}h2{font-size:1.25em}h3{font-size:1.1em}
p{margin:.5em 0}
table{border-collapse:collapse;width:100%;margin:1em 0;font-size:13px}
th,td{border:1px solid #e2e2e2;padding:8px 12px;text-align:left;vertical-align:top}
th{background:#f8f8f8;font-weight:600}
tr:nth-child(even){background:#fafafa}
img{max-width:100%;height:auto;margin:1em 0;border-radius:4px}
figure{margin:1.5em 0;text-align:center}
figcaption{font-size:12px;color:#666;margin-top:.5em;font-style:italic}
blockquote{border-left:3px solid #ddd;padding-left:1em;color:#555;margin:1em 0}
code{font-family:"SF Mono",Menlo,monospace;font-size:12px;background:#f4f4f4;padding:2px 4px;border-radius:3px}
pre{background:#f4f4f4;padding:1em;border-radius:6px;overflow-x:auto;font-size:12px}
a{color:#2563eb;text-decoration:none}a:hover{text-decoration:underline}
ul,ol{padding-left:1.5em;margin:.5em 0}li{margin:.25em 0}
hr{border:none;border-top:1px solid #eee;margin:2em 0}
</style>`;

const PREVIEW_HEAD = KATEX_IFRAME_HEAD + PREVIEW_STYLES;
const HEAD_TAG = /<head(?:\s[^>]*)?>/i;
const HTML_TAG = /<html(?:\s[^>]*)?>/i;

/**
 * Add trusted fallback styles to the sanitized document head. Report styles
 * follow these defaults in document order, so the original design wins.
 */
export const buildPreviewDocument = (content: string): string => {
  const sanitized = sanitizePreviewHtml(content);
  if (HEAD_TAG.test(sanitized)) {
    return sanitized.replace(HEAD_TAG, (head) => `${head}${PREVIEW_HEAD}`);
  }
  if (HTML_TAG.test(sanitized)) {
    return sanitized.replace(HTML_TAG, (html) => `${html}<head>${PREVIEW_HEAD}</head>`);
  }
  return `<!doctype html><html><head>${PREVIEW_HEAD}</head><body>${sanitized}</body></html>`;
};

/** Sandboxed iframe rendering of (sanitized) HTML content. */
export const HtmlRenderer = ({ content }: { content: string }) => {
  const srcDoc = useMemo(() => buildPreviewDocument(content), [content]);

  // No allow-same-origin: opaque iframe origin so preview HTML cannot touch the
  // parent. In-document #fragment navigation still works natively in srcDoc.
  return (
    <iframe
      srcDoc={srcDoc}
      sandbox="allow-popups"
      className="w-full border-0 bg-white"
      style={{ height: "calc(100vh - 8rem)", minHeight: "500px" }}
      title="HTML preview"
      referrerPolicy="no-referrer"
    />
  );
};
